import clsx from 'clsx'
import countries from 'flag-icons/country.json'
import { isEmail } from 'lib/utilities/string'
import { MouseEvent, useCallback, useEffect, useState } from 'react'
import { Control, SubmitHandler, useForm } from 'react-hook-form'
import AccountSection from '../AccountSection/AccountSection'
import Button from '../Button/Button'
import { PhoneInput, TextInput } from '../Form'
import ComboboxInput from '../Form/ComboboxInput/ComboboxInput'
import Label from './partial/Label'

export enum AccountType {
  Normal = 'normal',
  Pro = 'pro',
}

export interface AccountUser {
  clubName: string
  countryCode: string
  email: string
  firstName: string
  lastName: string
  pgaNumber?: string
  phoneNumber: string
}

export interface AccountPersonalInformationProps {
  /**
   * Indicates if the data is still loading
   */
  isLoading?: boolean
  /**
   * Callback function to handle form submission
   */
  onSubmit: SubmitHandler<AccountUser>
  /**
   * The type of account, which can be either 'normal' or 'pro'
   */
  type: AccountType
  /**
   * User's personal information
   */
  user: AccountUser
}

export default function AccountPersonalInformation({
  isLoading = false,
  onSubmit,
  type,
  user,
}: Readonly<AccountPersonalInformationProps>) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitSuccessful },
  } = useForm<AccountUser>()
  const [isEdit, setIsEdit] = useState(false)

  const handleEditProfile = useCallback((e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setIsEdit(true)
  }, [])

  const handleCancel = useCallback(() => {
    setIsEdit(false)
    reset(user)
  }, [reset, user])

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(user)
      setIsEdit(false)
    }
  }, [isSubmitSuccessful, reset, user])

  return (
    <AccountSection
      title="Personal information"
      link={
        !isEdit
          ? {
              title: 'Edit profile',
              onClick: handleEditProfile,
              type: 'button',
            }
          : undefined
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <address
          className={clsx(
            'grid gap-x-6 gap-y-4 text-neutral-700 not-italic',
            !isEdit
              ? "grid-cols-2 md:grid-cols-3 md:[grid-template-areas:'a_b_c''e_d_f''g_._.']"
              : 'grid-cols-1 md:grid-cols-2',
          )}
        >
          {!isEdit && (
            <>
              <Label
                className="md:[grid-area:a]"
                isLoading={isLoading}
                label="First name"
                value={user.firstName}
              />
              <Label
                className="md:[grid-area:b]"
                isLoading={isLoading}
                label="Last name"
                value={user.lastName}
              />
              <Label
                className="md:[grid-area:c]"
                isLoading={isLoading}
                label="Country of residence"
                value={user.countryCode}
              />
              <Label
                className="md:[grid-area:d]"
                isLoading={isLoading}
                label="Phone number"
                value={user.phoneNumber}
              />
              <Label
                className="md:[grid-area:e]"
                isLoading={isLoading}
                label="Email address"
                value={user.email}
              />
              <Label
                className="md:[grid-area:f]"
                isLoading={isLoading}
                label="Golf facility"
                value={user.clubName || 'N/A'}
              />
              {type === 'pro' && (
                <Label
                  className="md:[grid-area:g]"
                  isLoading={isLoading}
                  label="PGA number"
                  value={user.pgaNumber || 'N/A'}
                />
              )}
            </>
          )}
          {isEdit && (
            <>
              <TextInput
                control={control as unknown as Control}
                name="firstName"
                label="First name"
                placeholder="Enter first name"
                className="col-span-full md:col-span-1"
                rules={{
                  required: {
                    value: true,
                    message: 'First name is required',
                  },
                }}
                defaultValue={user.firstName}
                contentEditable
              />
              <TextInput
                control={control as unknown as Control}
                name="lastName"
                label="Last name"
                placeholder="Enter last name"
                className="col-span-full md:col-span-1"
                rules={{
                  required: {
                    value: true,
                    message: 'Last name is required',
                  },
                }}
                defaultValue={user.lastName}
                contentEditable
              />
              <ComboboxInput
                control={control as unknown as Control}
                name="countryCode"
                label="Country of residence"
                placeholder="Choose country"
                rules={{
                  required: { value: true, message: 'Country is required' },
                }}
                options={countries.map(({ code, name }) => ({
                  value: code.toUpperCase(),
                  label: name,
                }))}
                className="col-span-full md:col-span-1"
                defaultValue={user.countryCode}
              />
              <TextInput
                name="email"
                label="Email address"
                placeholder="Enter email"
                control={control as unknown as Control}
                className="col-span-full md:col-span-1"
                rules={{
                  validate: (value) =>
                    isEmail(value) || 'Please provide a valid email',
                }}
                defaultValue={user.email}
                contentEditable
              />
              <PhoneInput
                name="phoneNumber"
                label="Phone number*"
                placeholder="555-555-5555"
                control={control as unknown as Control}
                className="col-span-full md:col-span-1"
                rules={{
                  required: {
                    value: true,
                    message: 'Phone number is required',
                  },
                }}
                defaultValue={user.phoneNumber}
              />
              <TextInput
                control={control as unknown as Control}
                name="clubName"
                label="Golf facility"
                placeholder="Enter golf facility"
                className="col-span-full md:col-span-1"
                defaultValue={user.clubName}
                contentEditable
              />
              {type === 'pro' && (
                <TextInput
                  control={control as unknown as Control}
                  name="pgaNumber"
                  label="PGA number"
                  placeholder="Enter PGA number"
                  className="md:col-span-2"
                  rules={{
                    required: {
                      value: true,
                      message: 'PGA number is required',
                    },
                  }}
                  defaultValue={user.pgaNumber || 'N/A'}
                  contentEditable
                />
              )}
              <div className="flex items-center gap-x-4 md:col-span-2">
                <Button type="submit" className="min-w-[120px]">
                  Save
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="secondary"
                  className="min-w-[120px]"
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </address>
      </form>
    </AccountSection>
  )
}
